import {isFeatureEnabled} from '@github-ui/feature-flags'
import type {RouteQueryKey} from '@github-ui/react-core/query-key'
import type {OrderDirection} from '@github-ui/server-persisted-queries/types/RepositoryLabelIndexPageQuery'
import {useSearchParams} from '@github-ui/use-navigate'
import {useCallback, useState} from 'react'
import {useRelayEnvironment} from 'react-relay'

import {LABELS} from './constants/labels'
import {DialogForm as CreateDialog} from './DialogForm'
import type {createRepositoryLabelMutation$data} from './mutations/__generated__/createRepositoryLabelMutation.graphql'
import {commitCreateRepositoryLabelMutation} from './mutations/create-repository-label-mutation'
import type {RepositoryNode} from './repository-label-index-page-query-types'
import {SORT_MAP, type SortKeyword, VALID_UI_SORT_DIRECTIONS} from './sort-options'
import {useCreateLabel} from './use-create-label'
import {formatFormErrorMessage} from './utils/format-form-error-message'

export type LabelCreateFragment = Pick<RepositoryNode, 'id' | 'nameWithOwner' | 'viewerCanPush'>

type LabelCreateProps = {
  repository: LabelCreateFragment
  isOpen: boolean
  setDialogOpen: (open: boolean) => void
  queryKey?: RouteQueryKey
}

export function LabelCreate(props: LabelCreateProps) {
  const dataRouterEnabled = isFeatureEnabled('issues_data_router')

  if (dataRouterEnabled) {
    return <LabelCreateNew {...props} />
  }

  return <LabelCreateRelay {...props} />
}

function LabelCreateNew({repository, isOpen, setDialogOpen, queryKey}: LabelCreateProps) {
  const [submissionErrors, setSubmissionErrors] = useState('')
  const [lastInput, setLastInput] = useState<{name: string; description: string; color: string} | undefined>()

  const createLabel = useCreateLabel({
    repositoryId: repository.id,
    repositoryNameWithOwner: repository.nameWithOwner,
    queryKey,
    setSubmissionErrors,
    setDialogOpen,
    onSuccess: () => setLastInput(undefined),
  })

  const handleSubmit = useCallback(
    async (
      input: {name: string; description: string; color: string},
      setIsSubmitting: (isSubmitting: boolean) => void,
    ) => {
      setSubmissionErrors('')
      setLastInput(input)

      if (!repository.viewerCanPush) {
        setSubmissionErrors(LABELS.labelCreatePermissionError)
        setIsSubmitting(false)
        return
      }

      await createLabel(input, setIsSubmitting)
    },
    [repository, createLabel],
  )

  const handleClose = useCallback(() => {
    setSubmissionErrors('')
    setLastInput(undefined)
    setDialogOpen(false)
  }, [setDialogOpen])

  if (!isOpen) {
    return null
  }

  return (
    <CreateDialog
      onDialogClose={handleClose}
      formTitle={LABELS.newLabel}
      namePlaceholder={LABELS.labelNamePlaceholder}
      descriptionPlaceholder={LABELS.labelDescriptionPlaceholder}
      submissionErrors={submissionErrors}
      submitButtonText={LABELS.createButtonText}
      onDialogSubmit={handleSubmit}
      initialValues={lastInput}
    />
  )
}

function getConnectionVars(sp: URLSearchParams) {
  const sortParam = sp.get('sort') ?? 'name-asc'
  const [sortKey, direction = 'asc'] = sortParam.split('-') as [string, 'asc' | 'desc' | undefined]

  const orderField = SORT_MAP[sortKey as SortKeyword] ?? SORT_MAP.name
  const orderDirection = VALID_UI_SORT_DIRECTIONS.includes(direction) ? direction : 'asc'

  return {
    first: 30,
    orderBy: {
      direction: orderDirection.toUpperCase() as OrderDirection,
      field: orderField,
    },
    skip: 0,
    ...(sp.get('q') && {query: sp.get('q')}),
  }
}

function LabelCreateRelay({repository, isOpen, setDialogOpen}: LabelCreateProps) {
  const environment = useRelayEnvironment()
  const [submissionErrors, setSubmissionErrors] = useState('')
  const [searchParams] = useSearchParams()

  const handleSubmit = useCallback(
    (input: {name: string; description: string; color: string}, setIsSubmitting: (isSubmitting: boolean) => void) => {
      setSubmissionErrors('')

      if (!repository.viewerCanPush) {
        setSubmissionErrors(LABELS.labelCreatePermissionError)
        setIsSubmitting(false)
        return
      }

      const connectionArgs = getConnectionVars(searchParams)

      commitCreateRepositoryLabelMutation({
        environment,
        input: {
          repositoryId: repository.id,
          name: input.name,
          color: input.color,
          description: input.description,
        },
        repositoryId: repository.id,
        connectionArgs,
        onError: (error: Error) => {
          setIsSubmitting(false)
          if (error.cause && Array.isArray(error.cause)) {
            setSubmissionErrors(formatFormErrorMessage(error.cause))
          } else {
            setSubmissionErrors(LABELS.labelCreateError)
          }
        },
        onCompleted: (response: createRepositoryLabelMutation$data) => {
          setIsSubmitting(false)
          if (response.createLabel?.errors && response.createLabel.errors.length > 0) {
            setSubmissionErrors(formatFormErrorMessage(response.createLabel.errors))
          } else {
            setDialogOpen(false)
          }
        },
      })
    },
    [environment, repository, setDialogOpen, searchParams],
  )

  const handleClose = useCallback(() => {
    setSubmissionErrors('')
    setDialogOpen(false)
  }, [setDialogOpen])

  if (!isOpen) {
    return null
  }

  return (
    <CreateDialog
      onDialogClose={handleClose}
      formTitle={LABELS.newLabel}
      namePlaceholder={LABELS.labelNamePlaceholder}
      descriptionPlaceholder={LABELS.labelDescriptionPlaceholder}
      submissionErrors={submissionErrors}
      submitButtonText={LABELS.createButtonText}
      onDialogSubmit={handleSubmit}
    />
  )
}
